import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { useCopyHistory } from "../hooks/useCopyLog";
import { usePortals } from "../../hooks/usePortals";
import type { CopyEntityType } from "../types";

interface Props {
  destPortalId: string;
}

const ENTITY_LABEL: Record<CopyEntityType, string> = {
  agent: "Agent",
  cms_page: "CMS Page",
  file: "File",
  llm_model: "LLM Model",
};

const ENTITY_COLOR: Record<
  CopyEntityType,
  "primary" | "secondary" | "default" | "info"
> = {
  agent: "primary",
  cms_page: "secondary",
  file: "default",
  llm_model: "info",
};

type FilterType = "all" | CopyEntityType;

const CopyHistoryPanel: React.FC<Props> = ({ destPortalId }) => {
  const { data, isLoading } = useCopyHistory(destPortalId);
  const { portals } = usePortals();
  const [entityFilter, setEntityFilter] = useState<FilterType>("all");
  const [sourceFilter, setSourceFilter] = useState<string>("all");

  // Build portal name lookup
  const portalNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const p of portals) {
      map[p.id] = p.name;
    }
    return map;
  }, [portals]);

  const items = useMemo(() => data?.items ?? [], [data]);

  // Unique source portal IDs in history
  const sourcePortalIds = useMemo(() => {
    const ids = [...new Set(items.map((e) => e.source_portal_id))];
    return ids.sort((a, b) =>
      (portalNameMap[a] ?? a).localeCompare(portalNameMap[b] ?? b),
    );
  }, [items, portalNameMap]);

  // Filtered items
  const filtered = useMemo(() => {
    let result = items;
    if (entityFilter !== "all") {
      result = result.filter((e) => e.entity_type === entityFilter);
    }
    if (sourceFilter !== "all") {
      result = result.filter((e) => e.source_portal_id === sourceFilter);
    }
    return result;
  }, [items, entityFilter, sourceFilter]);

  if (isLoading) {
    return (
      <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 3 }}>
        <CircularProgress size={18} />
        <Typography variant="body2">Loading history...</Typography>
      </Stack>
    );
  }

  if (items.length === 0) {
    return (
      <Alert severity="info">
        No items have been copied into this portal yet. Use the Agents, CMS
        Pages, or Files tabs to import content from another portal.
      </Alert>
    );
  }

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
        <Box>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
            Entity type
          </Typography>
          <Select
            value={entityFilter}
            onChange={(e) => setEntityFilter(e.target.value as FilterType)}
            size="small"
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="all">All types</MenuItem>
            <MenuItem value="agent">Agents</MenuItem>
            <MenuItem value="cms_page">CMS Pages</MenuItem>
            <MenuItem value="file">Files</MenuItem>
            <MenuItem value="llm_model">LLM Models</MenuItem>
          </Select>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
            Source portal
          </Typography>
          <Select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            size="small"
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="all">All portals</MenuItem>
            {sourcePortalIds.map((id) => (
              <MenuItem key={id} value={id}>
                {portalNameMap[id] ?? id.slice(0, 8) + "..."}
              </MenuItem>
            ))}
          </Select>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ pt: 2 }}>
          {filtered.length} of {items.length} entries
        </Typography>
      </Stack>

      <Paper
        elevation={0}
        sx={{ border: "1px solid", borderColor: "divider", overflow: "hidden" }}
      >
        {filtered.map((entry, i) => (
          <Stack
            key={entry.id}
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{
              px: 2,
              py: 1.25,
              borderTop: i === 0 ? "none" : "1px solid",
              borderColor: "divider",
            }}
          >
            <Chip
              label={ENTITY_LABEL[entry.entity_type] ?? entry.entity_type}
              size="small"
              color={ENTITY_COLOR[entry.entity_type] ?? "default"}
              variant="outlined"
              sx={{ minWidth: 90 }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600}>
                {entry.source_entity_name}
                {entry.renamed && (
                  <Typography
                    component="span"
                    variant="body2"
                    color="warning.main"
                    sx={{ ml: 1 }}
                  >
                    &rarr; {entry.dest_entity_name}
                  </Typography>
                )}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                from{" "}
                <strong>
                  {portalNameMap[entry.source_portal_id] ??
                    entry.source_portal_id.slice(0, 8) + "..."}
                </strong>
                {entry.notes ? ` · ${entry.notes}` : ""}
              </Typography>
            </Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ whiteSpace: "nowrap" }}
            >
              {new Date(entry.copied_at).toLocaleString()}
            </Typography>
          </Stack>
        ))}
        {filtered.length === 0 && (
          <Box sx={{ px: 2, py: 3, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              No entries match the selected filters.
            </Typography>
          </Box>
        )}
      </Paper>
    </Stack>
  );
};

export default CopyHistoryPanel;
